import React, { useState, useEffect } from 'react';
import { Sparkles, School, Target, TrendingUp, CheckCircle, AlertCircle, Loader2, ChevronDown, ChevronUp, Plus, X, Check, Info, Lightbulb, MapPin } from 'lucide-react';
import { useFamilyContext } from '../../contexts/FamilyContext';
import { useCollegeContext } from '../../contexts/CollegeContext';
import { getProgressiveInsights } from '../../data/collegeRecommendationInsights';
import { toast } from 'react-hot-toast';

const AICollegeRecommendations = ({ studentProfile, collegePreferences, collegeList = [], crisisMode = false }) => {
  const { currentUser, familyId } = useFamilyContext();
  const { addCollege, loadCollegeJourneyData } = useCollegeContext();
  const [activeTab, setActiveTab] = useState('safety');
  const [loading, setLoading] = useState(false);
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [currentInsight, setCurrentInsight] = useState(null);
  const [recommendations, setRecommendations] = useState({
    safety: null,
    target: null,
    reach: null
  });
  const [error, setError] = useState(null);
  const [expandedSchools, setExpandedSchools] = useState({});
  const [acceptedSchools, setAcceptedSchools] = useState({});
  const [rejectedSchools, setRejectedSchools] = useState({});

  // Auto-expand all schools when recommendations are loaded
  useEffect(() => {
    if (recommendations[activeTab]) {
      const expandedState = {};
      recommendations[activeTab].forEach((_, index) => {
        expandedState[index] = true; // Expand all by default
      });
      setExpandedSchools(expandedState);
    }
  }, [recommendations, activeTab]);
  const [recommendationHistory, setRecommendationHistory] = useState(null);
  const [selectedTimeline, setSelectedTimeline] = useState('junior');

  const tabConfig = {
    safety: {
      label: 'Safety Schools',
      icon: CheckCircle,
      color: 'teal',
      bgColor: 'bg-teal-500',
      hoverColor: 'hover:bg-teal-600',
      borderColor: 'border-teal-800',
      textColor: 'text-teal-600',
      lightBg: 'bg-teal-50',
      description: 'Schools where you have strong admission chances'
    },
    target: {
      label: 'Target Schools',
      icon: Target,
      color: 'amber',
      bgColor: 'bg-amber-500',
      hoverColor: 'hover:bg-amber-600',
      borderColor: 'border-amber-800',
      textColor: 'text-amber-600',
      lightBg: 'bg-amber-50',
      description: 'Schools where you are a competitive applicant'
    },
    reach: {
      label: 'Reach Schools',
      icon: TrendingUp,
      color: 'rose',
      bgColor: 'bg-rose-500',
      hoverColor: 'hover:bg-rose-600',
      borderColor: 'border-rose-800',
      textColor: 'text-rose-600',
      lightBg: 'bg-rose-50',
      description: 'Aspirational schools worth applying to'
    }
  };

  useEffect(() => {
    // Load recommendation history on mount
    if (currentUser?.id || studentProfile?.user_id) {
      loadRecommendationHistory();
    }
  }, [currentUser, studentProfile]);

  const fetchRecommendations = async (type) => {
    if (recommendations[type]) {
      return;
    }

    const userId = currentUser?.id || studentProfile?.user_id || studentProfile?.userId;

    if (!userId) {
      setError('User ID is required. Please ensure you are logged in.');
      return;
    }

    setLoading(true);
    setError(null);
    setLoadingProgress(0);

    // Get progressive insights for this type
    const insights = getProgressiveInsights(type);
    let currentInsightIndex = 0;

    // Simulate progressive loading with insights
    const progressInterval = setInterval(() => {
      if (currentInsightIndex < insights.length) {
        const insight = insights[currentInsightIndex];
        setLoadingProgress(insight.progress);
        setCurrentInsight(insight);
        currentInsightIndex++;
      }
    }, 3000); // Change insight every 3 seconds

    try {
      // Get rejected schools for this type from recommendation history
      const rejectedForType = recommendationHistory?.[type]?.rejected || [];

      const response = await fetch('/api/anthropic/suggestions', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: `generate-${type}-schools`,
          userId,
          existingColleges: collegeList,  // Pass existing colleges to avoid duplicates
          rejectedSchools: rejectedForType,  // Pass rejected schools to avoid re-suggesting
          timeline: selectedTimeline,  // Pass timeline for context-aware recommendations
          count: crisisMode ? 4 : 6,
          crisisMode
        })
      });

      if (!response.ok) {
        throw new Error(`Failed to generate ${type} schools`);
      }

      const data = await response.json();

      console.log(`✅ ${type} schools generated with:`, {
        hasStrategicProfile: data.metadata?.hasStrategicProfile,
        hasExecutiveSummary: data.metadata?.hasExecutiveSummary,
        statsAvailable: data.metadata?.statsAvailable
      });

      // Complete the progress
      clearInterval(progressInterval);
      setLoadingProgress(100);

      setRecommendations(prev => ({
        ...prev,
        [type]: data.schools
      }));

    } catch (err) {
      console.error(`Error fetching ${type} schools:`, err);
      setError(`Failed to generate ${type} school recommendations. Please try again.`);
      clearInterval(progressInterval);
    } finally {
      setTimeout(() => {
        setLoading(false);
        setLoadingProgress(0);
        setCurrentInsight(null);
      }, 500);
    }
  };

  const toggleSchoolExpansion = (index) => {
    setExpandedSchools(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  const handleTabChange = (type) => {
    setActiveTab(type);
    if (!recommendations[type]) {
      fetchRecommendations(type);
    }
  };

  const handleAcceptSchool = async (e, schoolName, type) => {
    e.stopPropagation();
    const userId = currentUser?.id || studentProfile?.user_id || studentProfile?.userId;

    if (!userId) {
      console.error('No user ID available');
      toast.error('Unable to add college - user not found');
      return;
    }

    try {
      // First, mark as accepted in recommendation history
      const response = await fetch('/api/anthropic/suggestions', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'accept-recommendation',
          userId,
          schoolName,
          type
        })
      });

      if (response.ok) {
        // Find the full school data
        const school = recommendations[type]?.find(s => s.schoolName === schoolName);

        if (school) {
          // Add to student's college list with the correct category
          const collegeData = {
            name: school.schoolName,
            official_name: school.schoolName,
            location: school.location,
            category: type, // 'safety', 'target', or 'reach'
            status: 'researching',
            application_round: 'regular'
          };

          await addCollege(collegeData, userId, familyId);

          // Update UI states
          setAcceptedSchools(prev => ({ ...prev, [schoolName]: true }));
          setRejectedSchools(prev => ({ ...prev, [schoolName]: false }));
          await loadRecommendationHistory();

          // Reload college data to show the newly added college
          await loadCollegeJourneyData(userId);

          // Show success message with category
          const categoryEmoji = {
            safety: '🛡️',
            target: '🎯',
            reach: '🚀'
          };
          toast.success(`${categoryEmoji[type]} Added ${schoolName} to your ${type} schools!`);
        }
      }
    } catch (error) {
      console.error('Failed to accept school:', error);
      toast.error('Failed to add college to your list');
    }
  };

  const handleRejectSchool = async (e, schoolName, type) => {
    e.stopPropagation();
    const userId = currentUser?.id || studentProfile?.user_id || studentProfile?.userId;

    if (!userId) {
      console.error('No user ID available');
      return;
    }

    try {
      const response = await fetch('/api/anthropic/suggestions', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'reject-recommendation',
          userId,
          schoolName,
          type
        })
      });

      if (response.ok) {
        setRejectedSchools(prev => ({ ...prev, [schoolName]: true }));
        setAcceptedSchools(prev => ({ ...prev, [schoolName]: false }));
        await loadRecommendationHistory();
      }
    } catch (error) {
      console.error('Failed to reject school:', error);
    }
  };

  const loadRecommendationHistory = async () => {
    const userId = currentUser?.id || studentProfile?.user_id || studentProfile?.userId;
    if (!userId) return;

    try {
      const response = await fetch(`/api/anthropic/suggestions?action=get-recommendation-history&userId=${userId}`);

      if (!response.ok) {
        throw new Error(`Failed to load recommendation history: ${response.status}`);
      }

      const data = await response.json();
      if (data.success) {
        setRecommendationHistory(data.recommendations);

        // Restore generated recommendations to state
        const restoredRecommendations = {
          safety: data.recommendations.safety?.generated || null,
          target: data.recommendations.target?.generated || null,
          reach: data.recommendations.reach?.generated || null
        };

        // Only set non-empty arrays
        const recommendationsToRestore = {};
        ['safety', 'target', 'reach'].forEach(type => {
          if (restoredRecommendations[type] && restoredRecommendations[type].length > 0) {
            recommendationsToRestore[type] = restoredRecommendations[type];
          } else {
            recommendationsToRestore[type] = null;
          }
        });

        setRecommendations(recommendationsToRestore);

        // Update accepted/rejected states based on history
        const accepted = {};
        const rejected = {};
        ['safety', 'target', 'reach'].forEach(type => {
          data.recommendations[type]?.accepted?.forEach(school => {
            accepted[school] = true;
          });
          data.recommendations[type]?.rejected?.forEach(school => {
            rejected[school] = true;
          });
        });
        setAcceptedSchools(accepted);
        setRejectedSchools(rejected);

        console.log('✅ Restored recommendations from database:', {
          safety: recommendationsToRestore.safety?.length || 0,
          target: recommendationsToRestore.target?.length || 0,
          reach: recommendationsToRestore.reach?.length || 0
        });
      }
    } catch (error) {
      console.error('Failed to load recommendation history:', error);
    }
  };

  const renderSchoolCard = (school, index, type) => {
    const isExpanded = expandedSchools[index];
    const config = tabConfig[type];

    return (
      <div
        key={index}
        className={`bg-white border ${config.borderColor} rounded-xl overflow-hidden transition-all hover:shadow-lg`}
      >
        <div
          className="p-5 md:p-6 cursor-pointer"
          onClick={() => toggleSchoolExpansion(index)}
        >
          <div className="flex items-start justify-between gap-3 sm:gap-4">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-3 mb-3">
                <School className={`w-4 sm:w-5 h-4 sm:h-5 ${config.textColor} flex-shrink-0`} />
                <h3 className="font-bold text-base sm:text-base text-slate-900 leading-tight">{school.schoolName}</h3>
              </div>
              <p className="text-sm text-slate-600 mb-3 flex items-center gap-1.5">
                <MapPin className="w-4 h-4 flex-shrink-0" />
                {school.location}
              </p>
              <div className="flex items-center gap-3 sm:gap-4 text-sm">
                <span className="text-slate-700">
                  <strong className="font-medium">Acceptance Rate:</strong> {(school.acceptanceRate * 100).toFixed(1)}%
                </span>
              </div>
            </div>
            <div className="flex flex-col items-end gap-2 flex-shrink-0">
              <button className={`flex items-center gap-1 px-3 py-2 rounded-lg transition-all font-normal ${
                isExpanded
                  ? `bg-${config.color}-100 text-${config.color}-700`
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}>
                <Info className="w-4 h-4" />
                <span className="text-xs font-normal">
                  {isExpanded ? 'Hide' : 'Show'} Details
                </span>
                {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              </button>
            </div>
          </div>

          {!isExpanded && (
            <div>
              <div className="mt-5 pt-4 border-t border-slate-200 text-sm text-slate-700 leading-relaxed line-clamp-2">
                {type === 'safety' && school.whySafety}
                {type === 'target' && school.whyGoodFit}
                {type === 'reach' && school.honestAssessment}
              </div>
              <div className="mt-4 flex items-center flex-wrap gap-2">
                <button
                  onClick={(e) => handleAcceptSchool(e, school.schoolName, type)}
                  className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium transition-all border ${
                    acceptedSchools[school.schoolName]
                      ? 'bg-emerald-50 border-emerald-600 text-emerald-700'
                      : 'bg-white border-emerald-500 text-emerald-600 hover:bg-emerald-50'
                  }`}
                >
                  {acceptedSchools[school.schoolName] ? (
                    <><Check className="w-4 h-4" /> Added</>
                  ) : (
                    <><Plus className="w-4 h-4" /> Add to List</>
                  )}
                </button>
                <button
                  onClick={(e) => handleRejectSchool(e, school.schoolName, type)}
                  className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium transition-all border ${
                    rejectedSchools[school.schoolName]
                      ? 'bg-red-50 border-red-600 text-red-700'
                      : 'bg-white border-slate-300 text-slate-600 hover:bg-red-50 hover:text-red-600 hover:border-red-300'
                  }`}
                >
                  <X className="w-4 h-4" />
                  {rejectedSchools[school.schoolName] ? 'Rejected' : 'Not Interested'}
                </button>
              </div>
            </div>
          )}
        </div>

        {isExpanded && (
          <div className={`px-5 md:px-6 pb-6 space-y-5 border-t-2 ${config.borderColor} pt-5`}>
            {type === 'safety' && (
              <>
                <div>
                  <h4 className="font-medium text-slate-900 mb-2">Why It's a Safety</h4>
                  <p className="text-sm text-slate-700 leading-relaxed">{school.whySafety}</p>
                </div>
                {school.whyGoodFit && (
                  <div className={`bg-${config.color}-50 rounded-lg p-3`}>
                    <h4 className="font-medium text-slate-900 mb-2">Why It's a Good Fit For You</h4>
                    <p className="text-sm text-slate-700 leading-relaxed">{school.whyGoodFit}</p>
                  </div>
                )}
                <div>
                  <h4 className="font-medium text-slate-900 mb-2">Program Strengths</h4>
                  <p className="text-sm text-slate-700 leading-relaxed">{school.programStrengths}</p>
                </div>
                <div>
                  <h4 className="font-medium text-slate-900 mb-2">Affordability</h4>
                  <p className="text-sm text-slate-700 leading-relaxed">{school.affordability}</p>
                </div>
                {school.specialConsiderations && (
                  <div className="bg-slate-50 rounded-lg p-3">
                    <h4 className="font-medium text-slate-900 mb-1 text-sm">Special Considerations</h4>
                    <p className="text-sm text-slate-700">{school.specialConsiderations}</p>
                  </div>
                )}
              </>
            )}

            {type === 'target' && (
              <>
                <div>
                  <h4 className="font-medium text-slate-900 mb-2">Admission Chances</h4>
                  <p className="text-sm text-slate-700 leading-relaxed">{school.admissionChances}</p>
                </div>
                <div>
                  <h4 className="font-medium text-slate-900 mb-2">Why It's a Good Fit</h4>
                  <p className="text-sm text-slate-700 leading-relaxed">{school.whyGoodFit}</p>
                </div>
                <div className={`bg-${config.color}-50 rounded-lg p-3`}>
                  <h4 className="font-medium text-slate-900 mb-2">How to Stand Out</h4>
                  <p className="text-sm text-slate-700 leading-relaxed">{school.howToStandOut}</p>
                </div>
                <div>
                  <h4 className="font-medium text-slate-900 mb-2">Financial Aid</h4>
                  <p className="text-sm text-slate-700 leading-relaxed">{school.financialAid}</p>
                </div>
              </>
            )}

            {type === 'reach' && (
              <>
                <div>
                  <h4 className="font-medium text-slate-900 mb-2">Honest Assessment</h4>
                  <p className="text-sm text-slate-700 leading-relaxed">{school.honestAssessment}</p>
                </div>
                <div>
                  <h4 className="font-medium text-slate-900 mb-2">Best Fit Reason</h4>
                  <p className="text-sm text-slate-700 leading-relaxed">{school.bestFitReason}</p>
                </div>
                <div className={`bg-${config.color}-50 rounded-lg p-3`}>
                  <h4 className="font-medium text-slate-900 mb-2">Application Strategy</h4>
                  <p className="text-sm text-slate-700 leading-relaxed">{school.applicationStrategy}</p>
                </div>
                <div className={`bg-amber-50 border border-amber-200 rounded-lg p-3`}>
                  <h4 className="font-medium text-amber-900 mb-1 text-sm">Reality Check</h4>
                  <p className="text-sm text-amber-800">{school.realityCheck}</p>
                </div>
              </>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="w-full max-w-6xl mx-auto p-4">
      {/* Header */}
      <div className="bg-slate-900 rounded-2xl p-6 md:p-8 mb-8 border border-slate-200 shadow-sm">
        <div className="flex items-center gap-3 sm:gap-4 mb-4">
          <div className="w-8 h-8  rounded-xl flex items-center justify-center flex-shrink-0">
            <Sparkles className="w-4 sm:w-5 h-4 sm:h-5 text-white" />
          </div>
          <h2 className="text-lg md:text-xl font-medium text-stone-50">
            AI College Recommendations
          </h2>
        </div>
        <p className="text-stone-50 text-sm md:text-base leading-relaxed">
          {crisisMode
            ? 'Get quick, personalized college recommendations to help you build a balanced list fast.'
            : 'Discover personalized schools that match your profile and goals - backed by AI analysis of your academic strengths and preferences.'}
        </p>
      </div>

      {/* Timeline Selector */}
      <div className="bg-white rounded-xl border border-slate-200 p-2 md:p-4 mb-8">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 sm:gap-4">
          <div className="flex-1">
            <h3 className="font-medium text-slate-900 mb-2 text-sm">Your Timeline</h3>
            <p className="text-xs text-slate-600 leading-relaxed">
              Recommendations will be tailored to your current stage in the college application process
            </p>
          </div>
          <select
            value={selectedTimeline}
            onChange={(e) => {
              setSelectedTimeline(e.target.value);
              // Clear current recommendations to force regeneration with new timeline
              setRecommendations({ safety: null, target: null, reach: null });
            }}
            className="px-4 py-3 border border-slate-700 rounded-lg text-sm font-normal cursor-pointer hover:border-blue-400 transition-colors bg-white"
          >
            <option value="sophomore">Sophomore Year or Earlier</option>
            <option value="summer-junior">Summer Before Junior Year</option>
            <option value="junior">Junior Year</option>
            <option value="summer-senior">Summer Before Senior Year</option>
            <option value="senior">Senior Year (Fall)</option>
          </select>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-3 mb-8 px-2 pb-2 max-w-full">
        {Object.entries(tabConfig).map(([type, config]) => {
          const Icon = config.icon;
          const isActive = activeTab === type;

          return (
            <button
              key={type}
              onClick={() => handleTabChange(type)}
              className={`flex items-center gap-2 px-6 py-3.5 rounded-lg font-medium transition-all whitespace-nowrap border ${
                isActive
                  ? `${config.bgColor} ${config.borderColor} text-white shadow-lg scale-105`
                  : `bg-white text-slate-700 border-slate-200 hover:border-slate-400`
              }`}
            >
              <Icon className="w-4 sm:w-5 h-4 sm:h-5" />
              {config.label}
              {recommendations[type] && (
                <span className={`ml-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${
                  isActive ? 'bg-white text-slate-900' : `${config.lightBg} ${config.textColor}`
                }`}>
                  {recommendations[type].length}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Content Area */}
      <div className="bg-white rounded-xl border border-slate-200 p-6 md:p-8">
        {/* Description & Count */}
        <div className="mb-8">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 sm:gap-4 mb-4">
            <p className="text-slate-600 text-base leading-relaxed">
              {tabConfig[activeTab].description}
            </p>
            {recommendationHistory?.[activeTab] && (
              <div className="flex items-center gap-2 text-sm flex-shrink-0">
                <span className="text-slate-600">
                  Active recommendations:
                </span>
                <span className={`font-medium text-base ${
                  (recommendationHistory[activeTab].generated?.length || 0) >= 10
                    ? 'text-amber-600'
                    : 'text-blue-600'
                }`}>
                  {recommendationHistory[activeTab].generated?.length || 0} of 10
                </span>
              </div>
            )}
          </div>
          {recommendationHistory?.[activeTab] && (recommendationHistory[activeTab].generated?.length || 0) >= 10 && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start gap-3">
              <AlertCircle className="w-4 sm:w-5 h-4 sm:h-5 text-amber-600 mt-0.5 flex-shrink-0" />
              <p className="text-sm text-amber-800 leading-relaxed">
                You've reached the maximum of 10 recommendations for this category. Mark schools as "Not Interested" or accept them to make room for new suggestions.
              </p>
            </div>
          )}
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-6 flex items-start gap-3">
            <AlertCircle className="w-4 sm:w-5 h-4 sm:h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="font-medium text-red-900 mb-1">Error</h4>
              <p className="text-sm text-red-700">{error}</p>
            </div>
          </div>
        )}

        {loading && (
          <div className="bg-gradient-to-br from-slate-50 to-blue-50 rounded-xl p-8 border border-slate-200">
            {/* Progress Bar */}
            <div className="mb-6">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-slate-700">
                  {currentInsight?.action || 'Initializing...'}
                </span>
                <span className="text-sm font-medium text-blue-600">{loadingProgress}%</span>
              </div>
              <div className="w-full h-2 bg-slate-200 rounded-full overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-blue-500 to-violet-500 transition-all duration-500 ease-out"
                  style={{ width: `${loadingProgress}%` }}
                />
              </div>
            </div>

            {/* Current Insight */}
            {currentInsight && (
              <div className="bg-white rounded-lg p-6 border border-blue-200 shadow-sm">
                <div className="flex items-start gap-3 sm:gap-4">
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-violet-500 rounded-lg flex items-center justify-center">
                      <Lightbulb className="w-6 h-6 text-white" />
                    </div>
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-xs font-medium text-blue-600 uppercase tracking-wide">
                        {currentInsight.category}
                      </span>
                    </div>
                    <p className="text-slate-800 leading-relaxed font-normal">
                      {currentInsight.insight}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Loading Status */}
            <div className="flex items-center justify-center gap-3 mt-6 text-slate-600">
              <Loader2 className="w-4 sm:w-5 h-4 sm:h-5 animate-spin" />
              <p className="text-sm font-normal">
                Generating personalized {activeTab} school recommendations...
              </p>
            </div>
          </div>
        )}

        {!loading && !recommendations[activeTab] && (
          <div className="text-center py-12 px-6">
            <div className="max-w-2xl mx-auto">
              {/* Icon and Title */}
              <div className={`w-20 h-20 ${tabConfig[activeTab].lightBg} rounded-2xl flex items-center justify-center mx-auto mb-6 border ${tabConfig[activeTab].borderColor}`}>
                {React.createElement(tabConfig[activeTab].icon, { className: `w-10 h-10 ${tabConfig[activeTab].textColor}` })}
              </div>

              <h3 className="text-base font-medium text-slate-900 mb-3">
                Discover Your {tabConfig[activeTab].label}
              </h3>

              {/* Value Proposition */}
              <div className="bg-slate-50 rounded-xl p-6 mb-6 text-left">
                <p className="text-slate-700 leading-relaxed mb-4">
                  {activeTab === 'safety' && "We'll identify schools where you have strong admission chances AND excellent program fit. These aren't just backups - they're solid options where you'll thrive academically and financially."}
                  {activeTab === 'target' && "We'll find schools where you're a competitive applicant with realistic admission chances. These form the core of a balanced college list - places where you belong and can see yourself succeeding."}
                  {activeTab === 'reach' && "We'll suggest aspirational schools worth applying to, with honest assessments and specific strategies. You'll know exactly what makes each one a reach and how to position yourself as a compelling candidate."}
                </p>

                <div className="flex items-start gap-3 text-sm text-slate-600">
                  <CheckCircle className="w-4 sm:w-5 h-4 sm:h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                  <span>Personalized based on your academic profile and preferences</span>
                </div>
                <div className="flex items-start gap-3 text-sm text-slate-600 mt-2">
                  <CheckCircle className="w-4 sm:w-5 h-4 sm:h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                  <span>Strategic insights for each recommended school</span>
                </div>
                <div className="flex items-start gap-3 text-sm text-slate-600 mt-2">
                  <CheckCircle className="w-4 sm:w-5 h-4 sm:h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                  <span>Honest assessments to help you make informed decisions</span>
                </div>
              </div>

              {/* CTA Button */}
              <button
                onClick={() => fetchRecommendations(activeTab)}
                className={`px-8 py-4 ${tabConfig[activeTab].bgColor} ${tabConfig[activeTab].hoverColor} text-white rounded-lg font-normal text-sm border ${tabConfig[activeTab].borderColor} hover:shadow-xl hover:scale-105 transition-all flex items-center gap-3 mx-auto`}
              >
                <Sparkles className="w-4 h-4" />
                Find My {tabConfig[activeTab].label}
              </button>
            </div>
          </div>
        )}

        {!loading && recommendations[activeTab] && (
          <div className="space-y-5">
            {recommendations[activeTab].map((school, index) =>
              renderSchoolCard(school, index, activeTab)
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AICollegeRecommendations;